/**
 * Page Gestion Véhicules HAL 3000 - v1.0
 * 
 * Affiche tous les véhicules connectés avec:
 * - État batterie/carburant
 * - Position GPS
 * - Alertes autonomie
 * - Détection mouvements
 */
import { useState, useEffect, useCallback } from 'react';
import Layout from '@/components/Layout';
import { api } from '@/App';
import {
  Car, Bike, Battery, BatteryLow, BatteryCharging, 
  MapPin, Lock, Unlock, Zap, AlertTriangle, RefreshCw,
  Navigation, Gauge, Thermometer, Clock, Bell, Power
} from 'lucide-react';

const RANGE_ALERT_THRESHOLD = 50; // km

// Composant Chargeur VE 7,4 kW
const EVChargerCard = ({ charger, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [powerSetting, setPowerSetting] = useState(32); // Ampères par défaut
  
  if (!charger) {
    return (
      <div className="p-4 rounded-xl border-2 border-dashed border-green-500/30 bg-black">
        <div className="flex items-center gap-4">
          <Zap className="w-12 h-12 text-green-500/40" />
          <div>
            <div className="text-xl font-mono font-bold text-green-500/60">Chargeur VE 7,4 kW</div>
            <div className="text-sm font-mono text-gray-600">Non détecté</div>
          </div>
        </div>
      </div>
    );
  }
  
  const startCharging = async () => {
    setLoading(true);
    try {
      await api.post(`/integrations/tuya/devices/${charger.id}/commands`, {
        commands: [{ code: 'switch', value: true }]
      });
      await new Promise(r => setTimeout(r, 1500));
      onRefresh?.();
    } catch (e) {
      console.error('Error starting charge:', e);
      alert('Erreur lors du démarrage de la charge');
    }
    setLoading(false);
  };
  
  const stopCharging = async () => {
    setLoading(true);
    try {
      await api.post(`/integrations/tuya/devices/${charger.id}/commands`, {
        commands: [{ code: 'switch', value: false }]
      });
      await new Promise(r => setTimeout(r, 1500));
      onRefresh?.();
    } catch (e) {
      console.error('Error stopping charge:', e);
      alert('Erreur lors de l\'arrêt de la charge');
    }
    setLoading(false);
  };
  
  const getStateLabel = (state) => {
    const states = {
      'charger_free': 'DISPONIBLE',
      'charger_insert': 'CÂBLE INSÉRÉ',
      'charging': 'EN CHARGE',
      'charge_done': 'CHARGE TERMINÉE',
      'charge_stop': 'CHARGE ARRÊTÉE',
      'fault': 'DÉFAUT',
    };
    return states[state] || state?.toUpperCase() || 'INCONNU';
  };
  
  const isActive = charger.isCharging || charger.switchOn;
  
  const adjustPower = async (amps) => {
    setPowerSetting(amps);
    // TODO: Implémenter le contrôle de puissance via API Tuya
    console.log(`Setting charger power to ${amps}A`);
  };
  
  return (
    <div 
      className={`p-4 rounded-xl border-2 transition-all ${charger.isCharging ? 'animate-pulse' : ''}`}
      style={{ 
        borderColor: charger.isCharging ? '#22c55e' : '#22c55e50',
        background: charger.isCharging ? 'rgba(34, 197, 94, 0.15)' : 'rgba(34, 197, 94, 0.05)',
        boxShadow: charger.isCharging ? '0 0 30px rgba(34, 197, 94, 0.3)' : 'none'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-3 rounded-full ${charger.isCharging ? 'bg-green-500/30' : 'bg-green-900/50'}`}>
            <Zap className={`w-8 h-8 ${charger.isCharging ? 'text-green-400 animate-pulse' : 'text-green-500'}`} />
          </div>
          <div>
            <div className="text-xl font-mono font-bold text-green-400">CHARGEUR VE 7,4 kW</div>
            <div className={`text-sm font-mono ${charger.isCharging ? 'text-green-300' : 'text-gray-500'}`}>
              {getStateLabel(charger.workState)}
            </div>
          </div>
        </div>
        {!charger.online && (
          <div className="px-3 py-1 rounded-full bg-red-900/50 border border-red-500 text-red-400 text-xs font-mono">
            HORS LIGNE
          </div>
        )}
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="p-3 rounded-lg bg-black/50 border border-green-500/20 text-center">
          <div className="text-2xl font-mono font-bold text-green-400">{charger.lastChargeKwh || charger.energy || 0}</div>
          <div className="text-xs font-mono text-gray-500">kWh Session</div>
        </div>
        <div className="p-3 rounded-lg bg-black/50 border border-yellow-500/20 text-center">
          <div className="text-2xl font-mono font-bold text-yellow-400">{charger.lastChargeCost?.toFixed(2) || '0.00'}€</div>
          <div className="text-xs font-mono text-gray-500">Coût Session</div>
        </div>
        <div className="p-3 rounded-lg bg-black/50 border border-cyan-500/20 text-center">
          <div className="text-2xl font-mono font-bold text-cyan-400">{charger.totalEnergyKwh || 0}</div>
          <div className="text-xs font-mono text-gray-500">kWh Total</div>
        </div>
        <div className="p-3 rounded-lg bg-black/50 border border-orange-500/20 text-center">
          <div className="text-2xl font-mono font-bold text-orange-400">{charger.totalCost?.toFixed(2) || '0.00'}€</div>
          <div className="text-xs font-mono text-gray-500">Coût Total</div>
        </div>
      </div>
      
      {/* Power Control */}
      <div className="mb-4 p-3 rounded-lg bg-black/30 border border-green-500/20">
        <div className="text-xs font-mono text-gray-500 mb-2">RÉGLAGE PUISSANCE</div>
        <div className="flex gap-2 justify-center">
          {[8, 10, 13, 16, 20, 25, 32].map(amps => (
            <button
              key={amps}
              onClick={() => adjustPower(amps)}
              className={`px-2 py-1 rounded font-mono text-sm transition-all ${
                powerSetting === amps 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {amps}A
            </button>
          ))}
        </div>
        <div className="text-center text-xs text-gray-600 mt-2">
          Puissance: {(powerSetting * 230 / 1000).toFixed(1)} kW
        </div>
      </div>
      
      {/* Control Buttons */}
      <div className="flex gap-3">
        {!isActive ? (
          <button
            onClick={startCharging}
            disabled={loading || !charger.online}
            className="flex-1 py-3 rounded-lg font-mono font-bold text-white bg-green-600 hover:bg-green-500 disabled:bg-gray-700 disabled:text-gray-500 transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <BatteryCharging className="w-5 h-5" />
                DÉMARRER CHARGE
              </>
            )}
          </button>
        ) : (
          <button
            onClick={stopCharging}
            disabled={loading}
            className="flex-1 py-3 rounded-lg font-mono font-bold text-white bg-red-600 hover:bg-red-500 disabled:bg-gray-700 transition-all flex items-center justify-center gap-2"
          >
            {loading ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Power className="w-5 h-5" />
                ARRÊTER CHARGE
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );
};

const VehicleCard = ({ vehicle, type, onAlert }) => {
  const data = vehicle?.data;
  const Icon = type === 'mg4' ? Car : Bike;
  const name = type === 'mg4' ? 'MG4 Electric' : 'NIU RQi 125';
  const color = type === 'mg4' ? '#3b82f6' : '#10b981';
  
  if (!vehicle?.connected && !vehicle?.data) {
    return (
      <div className="p-4 rounded-xl border-2 border-dashed" style={{ borderColor: `${color}30`, background: '#0a0a0a' }}>
        <div className="flex items-center gap-4">
          <Icon className="w-12 h-12" style={{ color: `${color}40` }} />
          <div>
            <div className="text-xl font-mono font-bold" style={{ color: `${color}60` }}>{name}</div>
            <div className="text-sm font-mono text-gray-600">
              {vehicle?.token_required ? 'Token requis - Voir Admin' : vehicle?.error || 'Non connecté - Cliquez Actualiser'}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const batteryPct = data?.battery_pct;
  const rangeKm = data?.range_km;
  const isCharging = data?.charging;
  const isLocked = data?.locked;
  const gps = data?.gps;
  const odometer = data?.odometer_km;
  const isLowRange = rangeKm && rangeKm < RANGE_ALERT_THRESHOLD;
  
  // Données de charge
  const chargingRemainingMin = data?.charging_remaining_min;
  const targetSoc = data?.target_soc;
  const chargeStats = data?.charge_stats;
  const realtimePowerKw = chargeStats?.realtime_power_kw;
  const gunConnected = chargeStats?.gun_connected;
  
  // Convertir coordonnées GPS en adresse approximative (ville)
  const getLocationName = (lat, lon) => {
    // Coordonnées connues de la maison
    if (lat && lon && Math.abs(lat - 48.8058) < 0.01 && Math.abs(lon - 2.1394) < 0.01) {
      return 'À domicile (Vélizy)';
    }
    return lat && lon ? `${lat.toFixed(4)}°N, ${lon.toFixed(4)}°E` : null;
  };
  
  const getBatteryColor = (pct) => {
    if (pct < 20) return '#ef4444';
    if (pct < 50) return '#f97316';
    return '#22c55e';
  };
  
  const formatChargingTime = (minutes) => {
    if (!minutes) return null;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}` : `${mins}min`;
  };
  
  const BatteryIcon = isCharging ? BatteryCharging : (batteryPct < 20 ? BatteryLow : Battery);
  
  return (
    <div 
      className={`p-4 rounded-xl border-2 transition-all ${isLowRange ? 'animate-pulse' : ''}`}
      style={{ 
        borderColor: isLowRange ? '#ef4444' : `${color}50`, 
        background: isLowRange ? 'rgba(239, 68, 68, 0.1)' : `${color}08`,
        boxShadow: isCharging ? `0 0 30px ${color}30` : 'none'
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Icon className="w-10 h-10" style={{ color }} />
          <div>
            <div className="text-xl font-mono font-bold" style={{ color }}>{name}</div>
            <div className="text-sm font-mono text-gray-500">
              {odometer && odometer > 0 ? `${odometer.toLocaleString()} km` : 'Kilométrage non disponible'}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {data?.from_cache && (
            <div className="px-2 py-0.5 rounded text-[9px] font-mono bg-gray-800 text-gray-500 border border-gray-700">
              {data?.stale ? 'CACHE PÉRIMÉ' : 'CACHE'}
            </div>
          )}
          {isCharging && (
            <div className="flex items-center gap-1 px-3 py-1 rounded-full bg-green-900/50 border border-green-500">
              <Zap className="w-4 h-4 text-green-400 animate-pulse" />
              <span className="text-sm font-mono text-green-400">EN CHARGE</span>
            </div>
          )}
          {isLocked !== undefined && (
            isLocked 
              ? <Lock className="w-6 h-6 text-green-400" />
              : <Unlock className="w-6 h-6 text-yellow-400 animate-pulse" />
          )}
        </div>
      </div>
      
      {/* Alert Banner */}
      {isLowRange && !isCharging && (
        <div className="mb-4 p-3 rounded-lg bg-red-900/50 border border-red-500 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-red-400 animate-bounce" />
          <div>
            <div className="text-red-400 font-mono font-bold">ALERTE AUTONOMIE</div>
            <div className="text-red-300 text-sm font-mono">Autonomie critique - Recharger le véhicule</div>
          </div>
          <Bell className="w-5 h-5 text-red-400 ml-auto" />
        </div>
      )}
      
      {/* Main Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        {/* Battery */}
        <div className="p-4 rounded-lg bg-black/30">
          <div className="flex items-center gap-2 mb-2">
            <BatteryIcon className="w-5 h-5" style={{ color: getBatteryColor(batteryPct) }} />
            <span className="text-sm font-mono text-gray-400">BATTERIE</span>
          </div>
          <div className="text-4xl font-bold font-mono" style={{ color: getBatteryColor(batteryPct) }}>
            {batteryPct?.toFixed(0) || '--'}%
          </div>
          {/* Battery bar */}
          <div className="mt-2 h-2 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full rounded-full transition-all"
              style={{ 
                width: `${batteryPct || 0}%`, 
                backgroundColor: getBatteryColor(batteryPct),
                boxShadow: `0 0 10px ${getBatteryColor(batteryPct)}`
              }} 
            />
          </div>
          {targetSoc && (
            <div className="text-xs text-gray-500 font-mono mt-1">Cible: {targetSoc}%</div>
          )}
        </div>
        
        {/* Range */}
        <div className={`p-4 rounded-lg ${isLowRange ? 'bg-red-900/30' : 'bg-black/30'}`}>
          <div className="flex items-center gap-2 mb-2">
            <Gauge className="w-5 h-5" style={{ color: isLowRange ? '#ef4444' : '#22c55e' }} />
            <span className="text-sm font-mono text-gray-400">AUTONOMIE</span>
          </div>
          <div className="text-4xl font-bold font-mono" style={{ color: isLowRange ? '#ef4444' : '#22c55e' }}>
            {rangeKm || '--'}
            <span className="text-lg ml-1">km</span>
          </div>
        </div>
      </div>
      
      {/* Charging Info - Only show when charging */}
      {isCharging && (
        <div className="p-4 rounded-lg bg-green-900/20 border border-green-500/30 mb-4">
          <div className="flex items-center gap-2 mb-3">
            <Zap className="w-5 h-5 text-green-400" />
            <span className="text-sm font-mono text-green-400">CHARGE EN COURS</span>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-2xl font-bold font-mono text-green-400">
                {realtimePowerKw ? `${realtimePowerKw}` : '--'}
              </div>
              <div className="text-xs text-gray-500">kW</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono text-cyan-400">
                {formatChargingTime(chargingRemainingMin) || '--'}
              </div>
              <div className="text-xs text-gray-500">Restant</div>
            </div>
            <div>
              <div className="text-2xl font-bold font-mono text-yellow-400">
                {targetSoc || '--'}%
              </div>
              <div className="text-xs text-gray-500">Cible</div>
            </div>
          </div>
        </div>
      )}
      
      {/* GPS & Location */}
      <div className="p-4 rounded-lg bg-black/30 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <MapPin className="w-5 h-5 text-cyan-400" />
          <span className="text-sm font-mono text-gray-400">POSITION</span>
        </div>
        {gps?.latitude && gps?.longitude ? (
          <div>
            <div className="text-lg font-mono text-cyan-400">
              {getLocationName(gps.latitude, gps.longitude)}
            </div>
            {gps.speed > 0 && (
              <div className="flex items-center gap-2 mt-1 text-yellow-400">
                <Navigation className="w-4 h-4" />
                <span className="font-mono">{gps.speed} km/h</span>
              </div>
            )}
          </div>
        ) : (
          <div className="text-gray-500 font-mono">Position non disponible</div>
        )}
      </div>
      
      {/* Additional Info */}
      <div className="grid grid-cols-3 gap-2 text-center">
        {data?.exterior_temp && data.exterior_temp > -100 && (
          <div className="p-2 rounded bg-black/20">
            <Thermometer className="w-4 h-4 mx-auto text-blue-400 mb-1" />
            <div className="text-sm font-mono text-gray-400">Ext.</div>
            <div className="font-mono font-bold text-blue-400">{data.exterior_temp}°C</div>
          </div>
        )}
        {gunConnected !== undefined && (
          <div className="p-2 rounded bg-black/20">
            <Zap className="w-4 h-4 mx-auto mb-1" style={{ color: gunConnected ? '#22c55e' : '#666' }} />
            <div className="text-sm font-mono text-gray-400">Câble</div>
            <div className="font-mono font-bold" style={{ color: gunConnected ? '#22c55e' : '#666' }}>
              {gunConnected ? 'Connecté' : 'Non'}
            </div>
          </div>
        )}
        {data?.last_update && (
          <div className="p-2 rounded bg-black/20">
            <Clock className="w-4 h-4 mx-auto text-gray-400 mb-1" />
            <div className="text-sm font-mono text-gray-400">MàJ</div>
            <div className="font-mono text-xs text-gray-500">{new Date(data.last_update).toLocaleTimeString('fr-FR')}</div>
          </div>
        )}
      </div>
    </div>
  );
};

const VehiclesPage = () => {
  const [loading, setLoading] = useState(true);
  const [vehicles, setVehicles] = useState(null);
  const [evCharger, setEvCharger] = useState(null);
  const [alertSent, setAlertSent] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [cacheInfo, setCacheInfo] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [vehiclesRes, chargerRes, cacheRes] = await Promise.all([
        api.get('/integrations/vehicles/status'),
        api.get('/integrations/vehicles/charger/status'),
        api.get('/integrations/vehicles/mg4/cache-info').catch(() => ({ data: null })),
      ]);
      
      if (vehiclesRes.data) {
        setVehicles(vehiclesRes.data);
      }
      if (cacheRes?.data) setCacheInfo(cacheRes.data);
      
      // Chargeur VE depuis l'API dédiée
      if (chargerRes.data?.success) {
        const charger = chargerRes.data.charger;
        setEvCharger({
          id: charger.device_id,
          name: charger.name,
          online: true,
          workState: charger.status,
          statusFr: charger.status_fr,
          energy: charger.last_charge_kwh || 0,
          lastChargeKwh: charger.last_charge_kwh || 0,
          lastChargeCost: charger.last_charge_cost || 0,
          totalEnergyKwh: charger.total_energy_kwh || 0,
          totalCost: charger.total_cost || 0,
          switchOn: charger.is_plugged,
          isCharging: charger.is_charging,
          powerKw: charger.power_kw,
          electricityPrice: charger.electricity_price,
        });
      }
    } catch (e) {
      console.error('Error loading vehicles:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      const res = await api.post('/integrations/vehicles/mg4/refresh');
      if (res.data?.success) {
        await loadData();
      }
    } catch (e) {
      console.error('Error refreshing MG4:', e);
    } finally {
      setRefreshing(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await api.post('/integrations/vehicles/mg4/disconnect');
      await loadData();
    } catch (e) {
      console.error('Error disconnecting MG4:', e);
    }
  };

  useEffect(() => {
    loadData();
    // Polling long (5min) - juste pour rafraîchir le cache info, pas de re-login
    const interval = setInterval(loadData, 300000);
    return () => clearInterval(interval);
  }, [loadData]);

  if (loading) {
    return (
      <Layout title="Gestion Véhicules">
        <div className="h-full flex items-center justify-center">
          <div className="text-cyan-400 font-mono animate-pulse">CHARGEMENT VÉHICULES...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Gestion Véhicules">
      <div className="h-full flex flex-col p-4" style={{ background: '#050505' }}>
        
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="text-lg font-mono font-bold text-cyan-400">
            VÉHICULES CONNECTÉS
          </div>
          <div className="flex items-center gap-2">
            {/* Cache indicator */}
            {cacheInfo && (
              <div className={`px-3 py-1.5 rounded-lg text-xs font-mono ${
                cacheInfo.stale
                  ? 'bg-orange-900/30 border border-orange-500/30 text-orange-400'
                  : 'bg-green-900/30 border border-green-500/30 text-green-400'
              }`} data-testid="mg4-cache-indicator">
                {cacheInfo.has_cache
                  ? `MàJ ${cacheInfo.cache_age_text}`
                  : 'Pas de données'}
              </div>
            )}
            {/* Disconnect button */}
            <button
              onClick={handleDisconnect}
              className="px-3 py-1.5 rounded-lg bg-gray-800 border border-gray-600 text-gray-400 hover:text-red-400 hover:border-red-500/50 transition-all text-xs font-mono"
              title="Déconnecter la session MG4 (libère l'API pour l'app mobile)"
              data-testid="mg4-disconnect-btn"
            >
              <Power className="w-4 h-4" />
            </button>
            {/* Manual refresh button */}
            <button 
              onClick={handleManualRefresh}
              disabled={refreshing}
              className={`px-4 py-1.5 rounded-lg border text-sm font-mono flex items-center gap-2 transition-all ${
                refreshing
                  ? 'bg-cyan-900/50 border-cyan-500/50 text-cyan-300 animate-pulse'
                  : 'bg-cyan-900/30 border-cyan-500/30 text-cyan-400 hover:bg-cyan-900/50'
              }`}
              data-testid="mg4-refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
              {refreshing ? 'Connexion...' : 'Actualiser MG4'}
            </button>
          </div>
        </div>

        {/* Stale data warning */}
        {vehicles?.mg4?.data?.stale && (
          <div className="mb-3 p-2 rounded-lg bg-orange-900/20 border border-orange-500/20 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-orange-400 flex-shrink-0" />
            <span className="text-xs font-mono text-orange-400/80">
              Données en cache (session expirée). Cliquez "Actualiser MG4" pour rafraîchir.
            </span>
          </div>
        )}
        
        {/* Vehicle Grid */}
        <div className="grid grid-cols-2 gap-4 flex-1 overflow-auto">
          {vehicles && (
            <>
              <VehicleCard vehicle={vehicles.mg4} type="mg4" />
              <VehicleCard vehicle={vehicles.niu} type="niu" />
            </>
          )}
          {/* Chargeur VE */}
          <EVChargerCard charger={evCharger} onRefresh={loadData} />
        </div>
        
        {/* Footer */}
        <div className="mt-4 p-3 rounded-lg border border-gray-800 bg-gray-900/30">
          <div className="flex items-center justify-between text-sm font-mono text-gray-500">
            <span>Mode manuel - Pas de polling automatique</span>
            <span>Cache: 1h | Seuil alerte: {RANGE_ALERT_THRESHOLD} km</span>
            <span>Session: {vehicles?.mg4?.connected ? '🟢 Active' : '⚪ Inactive'}</span>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default VehiclesPage;
